import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import ProgressIndicator from 'components/common/progress-indicator';
import { LevelType } from 'data/types';
import { setActiveLevelIndex } from 'features/ui';
import useIsPtacPathway from 'hooks/use-is-ptac-pathway';
import { useMemo } from 'react';
import {
  isAirHandlersStepCompleteForLevel,
  isLinesetsStepCompleteForLevel,
  isZonesStepCompleteForLevel
} from 'utils/configurations';
import { hasFootprintPolygons } from 'utils/footprint-polygons';
import { ptacUnitsStepCompleteForLevel } from 'utils/ptac-configuration-sections';
import { isLevelConditioned } from 'utils/structure';
import {
  isAddSpacesLevelComplete,
  levelRequiresSpaces
} from 'utils/structure-add-spaces';

type LevelPickerLevelProps = Readonly<{
  level: LevelType;
  index: number;
  currentSectionId?: string;
  onLevelClick?: (level: LevelType) => void;
}>;

export default function LevelPickerLevel({
  level,
  index,
  currentSectionId,
  onLevelClick
}: LevelPickerLevelProps) {
  const dispatch = useAppDispatch();
  const { activeLevelIndex } = useAppSelector((state) => state.ui);
  const { configurations, activeConfig } = useAppSelector(
    (state) => state.configurations
  );
  const { footprint } = useAppSelector((state) => state.structure);
  const isPtacPathway = useIsPtacPathway();
  const currentConfig = configurations[activeConfig];
  const isSelected = index === activeLevelIndex;
  const hasSpaces = level.sections?.some(
    (section) => section.spaces.length > 0
  );

  const isStepComplete = useMemo(() => {
    switch (currentSectionId) {
      case 'footprint':
        return hasFootprintPolygons(level);
      case 'zones':
        return isZonesStepCompleteForLevel(level, currentConfig);
      case 'air_handlers':
        return isPtacPathway
          ? ptacUnitsStepCompleteForLevel(level, currentConfig)
          : isAirHandlersStepCompleteForLevel(level, currentConfig);
      case 'line_sets':
        return isLinesetsStepCompleteForLevel(level, currentConfig);
      case 'add_spaces':
        return isAddSpacesLevelComplete(level);
      default:
        return hasSpaces;
    }
  }, [
    currentSectionId,
    level,
    currentConfig,
    hasSpaces,
    footprint,
    isPtacPathway
  ]);

  const isRequired =
    currentSectionId === 'footprint' ||
    (currentSectionId === 'add_spaces'
      ? levelRequiresSpaces(level)
      : isLevelConditioned(level));

  const handleClick = () => {
    if (onLevelClick) {
      onLevelClick(level);
    } else {
      dispatch(setActiveLevelIndex(index));
    }
  };

  return (
    <button
      type='button'
      id={`level-picker-level-${index}`}
      className={clsx(
        'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors focus:outline-none',
        isSelected && 'border-blue-500 bg-blue-500 text-white',
        !isSelected && 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
      )}
      onClick={handleClick}
    >
      <div className='flex flex-1 items-center gap-2'>
        <ProgressIndicator
          required={isRequired}
          checked={isStepComplete}
          size='sm'
        />
        {level.label}
      </div>
    </button>
  );
}
